import { WebSocketStatus } from '@tiptap-pro/provider'
import classNames from 'classnames'
import {
  type CSSProperties,
  useEffect,
  useRef,
  useState,
  type VFC,
} from 'react'
import { Icon } from '@opallabs/lapidary'
import * as styles from './styles'

/** How long the first connection may take before the banner reports it. */
const INITIAL_CONNECT_GRACE_MS = 10 * 1000
/**
 * How long a dropped connection may take to come back before the banner
 * reports it. The provider reconnects on its own, and a routine reconnect
 * (for example after the collaboration token expires) completes well within
 * this, so it stays silent.
 */
const RECONNECT_GRACE_MS = 5 * 1000
/** How long "Connection re-established!" stays up. */
const HIDE_DELAY_MS = 5 * 1000

type ConnectionBannerProps = {
  className?: string
  hasUnsyncedChanges: boolean
  status: WebSocketStatus | null
  style?: CSSProperties
}

export const ConnectionBanner: VFC<ConnectionBannerProps> = ({
  className,
  hasUnsyncedChanges,
  status,
  style,
}) => {
  const lastStatus = useRef<WebSocketStatus | null>(null)
  const hasConnected = useRef(false)
  const timer = useRef<NodeJS.Timeout>()
  const [isVisible, setVisibility] = useState(false)

  useEffect(() => {
    const prev = lastStatus.current
    lastStatus.current = status

    const isConnected = status === WebSocketStatus.Connected
    const wasConnected = prev === WebSocketStatus.Connected
    if (isConnected) {
      hasConnected.current = true
    }

    // Only a change between connected and not connected matters. Bouncing
    // between Disconnected and Connecting while the provider retries leaves
    // the pending timer running.
    if (status === null || (prev !== null && isConnected === wasConnected)) {
      return
    }

    clearTimeout(timer.current)
    if (isConnected) {
      timer.current = setTimeout(() => setVisibility(false), HIDE_DELAY_MS)
    } else {
      timer.current = setTimeout(
        () => setVisibility(true),
        hasConnected.current ? RECONNECT_GRACE_MS : INITIAL_CONNECT_GRACE_MS
      )
    }
  }, [status])

  useEffect(() => {
    const pending = timer
    return () => clearTimeout(pending.current)
  }, [])

  return (
    <div
      aria-hidden={!isVisible}
      css={styles.container}
      className={classNames('text-editor-connection-banner', className)}
      data-status={isVisible ? status : undefined}
      role="alert"
      style={style}
    >
      <div css={styles.content}>
        <Icon
          ariaHidden
          glyph={status === 'connected' ? 'gem-check-circle' : 'gem-warning'}
          width={24}
          height={24}
        />
        <span>
          {status === 'connected'
            ? 'Connection re-established!'
            : 'Auto-saving paused while we attempt to re-establish connection.'}

          {status !== 'connected' &&
            hasUnsyncedChanges &&
            ' Some of your changes have not been saved.'}
        </span>
      </div>
    </div>
  )
}

ConnectionBanner.displayName = 'ConnectionBanner'
